/**
 * What counts as input worth starting a Project Summary for (5A / O-S9, C03).
 *
 * Inspector notes are stored as the HTML the notes editor publishes (Stage1NotesEditor sets `notes` to the field's raw innerHTML on every
 * input). Clearing the field leaves markup with nothing visible in it, most often `<p><br></p>`. Truthiness and length tests read that as
 * "has notes", so a visually empty editor started a paid Summary. Whitespace alone is the same case with no markup.
 *
 * ONE rule, shared by every path that decides "is there anything to summarise":
 *   - the Summary hook's admission (explicit and automatic),
 *   - the header Next enabling rule (canGoNext), and
 *   - the App effects that start a Summary for typed notes or after the operator resumes AI.
 * Authored text is never altered or hidden: this only DECIDES, it does not rewrite the notes.
 *
 * Evidence other than notes is counted by presence: a document, a project photo, a transcript, or a retained video (which includes a
 * reference-only or failed one: the file is the evidence even when it could not be transcribed).
 */

const NAMED_ENTITIES: Record<string, string> = {
    nbsp: ' ', ensp: ' ', emsp: ' ', thinsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'",
};

function decodeEntities(text: string): string {
    return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, body: string) => {
        if (body[0] === '#') {
            const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
            return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : ' ';
        }
        return NAMED_ENTITIES[body.toLowerCase()] ?? match;
    });
}

/** The text a reader would SEE in a notes value: tags, comments and invisible characters removed, entities decoded. */
export function visibleNotesText(value: unknown): string {
    if (value === null || value === undefined) return '';
    const raw = String(value);
    if (!raw) return '';
    const withoutHidden = raw
        .replace(/<!--[\s\S]*?-->/g, ' ')
        .replace(/<(script|style)\b[\s\S]*?<\/\1\s*>/gi, ' ')
        .replace(/<[^>]*>/g, ' ');
    return decodeEntities(withoutHidden)
        // zero-width and bidi marks are invisible; every other space-like character collapses to nothing visible
        .replace(/[​-‏⁠﻿­]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

/** True when the notes show at least one visible character. `<p><br></p>`, `&nbsp;` and whitespace do not. */
export function hasMeaningfulNotes(value: unknown): boolean {
    return visibleNotesText(value).length > 0;
}

export interface MeaningfulProjectInput {
    notes?: unknown;
    documentCount?: number;
    photoCount?: number;
    transcriptCount?: number;
    videoCount?: number;
}

/**
 * What the View / Next action on Data Entry does about the Project Summary (the footer's callback and the header's handleNext both ask):
 *   open      a Summary is held and either still fresh, or stale with nothing left to regenerate it from (5A / O-S9). The page it opens
 *             states the staleness itself; opening declares nothing fresh and generates nothing.
 *   generate  no Summary is held, or the held one is stale and there IS something to regenerate it from. The Summary hook's own
 *             admission still refuses empty input, so a refusal stays the hook's.
 */
export function summaryViewAction({ hasSummary, summaryNeedsRefresh, hasInput }: { hasSummary: boolean; summaryNeedsRefresh: boolean; hasInput: boolean }): 'open' | 'generate' {
    if (hasSummary && !summaryNeedsRefresh) return 'open';
    if (hasSummary && !hasInput) return 'open';
    return 'generate';
}

/** The single "is there anything to summarise" rule. */
export function hasMeaningfulProjectInput(input: MeaningfulProjectInput = {}): boolean {
    return hasMeaningfulNotes(input.notes)
        || Number(input.documentCount) > 0
        || Number(input.photoCount) > 0
        || Number(input.transcriptCount) > 0
        || Number(input.videoCount) > 0;
}

/**
 * Whether QuoteMaker may write or remake the summary by itself (App's 2 s watcher, and Continue after a notes pause). Before
 * the first summary a few typed words are not enough: the notes must run past 50 characters, or a file, photo or clip be in,
 * so the first keystrokes are not paid for (a clip counted from Slice C: a run an edit stopped on a clip-only job must be
 * able to start again by itself). Once a summary exists and the job changed, the summary's own admission rule applies
 * (hasMeaningfulProjectInput): under Full AI an out-of-date summary is remade even when the notes are short, a clip-only job
 * included, so nothing waits for a press (SPEC 2026-10-21 B2, Slice C C13).
 */
export function canAutoSummarise(input: MeaningfulProjectInput & { hasSummary?: boolean } = {}): boolean {
    if (input.hasSummary) return hasMeaningfulProjectInput(input);
    const notes = typeof input.notes === 'string' ? input.notes : '';
    return (hasMeaningfulNotes(notes) && notes.length > 50)
        || Number(input.documentCount) > 0
        || Number(input.photoCount) > 0
        || Number(input.transcriptCount) > 0
        || Number(input.videoCount) > 0;
}
